import type { ComponentProps, ElementType } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { EnergyClassScaleType } from 'graphql-ts-types';
import type {
  BackgroundColorProps,
  FontSizeProps,
  HeightProps,
  LineHeightProps,
  MarginLeftProps,
  ResponsiveValue,
  VerticalAlignProps,
  WidthProps,
} from 'styled-system';
import {
  background,
  backgroundColor,
  border,
  borderRight,
  borderWidth,
  color,
  fontFamily,
  fontSize,
  fontWeight,
  height,
  lineHeight,
  marginLeft,
  padding,
  position,
  space,
  verticalAlign,
  width,
} from 'styled-system';

import rangeOld from '../assets/icons/energy-tag-range-old.svg';
import range from '../assets/icons/energy-tag-range.svg';
import { useLocalization } from '../providers/LocaleProvider';
import { focusOutlineCSSWithOffset } from '../theme/styles';
import { boxSizing, outline, textDecoration, textShadow } from '../theme/system-utilities';
import { getEnergyClassMeta, getOldEnergyClassMeta } from '../utils/energyClass';
import { Flex } from './Flex';

type EnergyTagSize = 'small' | 'large';

interface EnergyTagProps {
  energyClass: string;
  scaleType?: EnergyClassScaleType;
  href?: string;
  onClick?: () => void;
  size?: EnergyTagSize;
}

interface SizeConfig {
  labelHeight: ResponsiveValue<string>;
  arrowSize: ResponsiveValue<string>;
  arrowBorderSize: ResponsiveValue<string>;
}

const SIZE_CONFIG: Record<EnergyTagSize, SizeConfig> = {
  small: {
    labelHeight: ['16px', '18px', null, '20px'],
    arrowSize: ['8px', '9px', null, '10px'],
    arrowBorderSize: ['9px', '10px', null, '11px'],
  },
  large: {
    labelHeight: ['24px', '28px', null, '32px'],
    arrowSize: ['12px', '14px', null, '16px'],
    arrowBorderSize: ['13px', '15px', null, '17px'],
  },
};

const Container = styled(Flex)<{ href?: string }>`
  ${padding};
  ${outline};
  ${border};
  ${fontSize};
  ${background};
  ${lineHeight};
  ${textDecoration};
  ${focusOutlineCSSWithOffset}
`;

const containerStyleProps = {
  padding: 0,
  border: 'none',
  outline: 'none',
  background: 'none',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  textDecoration: 'unset',
};

const ContainerWithPointer = styled(Container)`
  cursor: pointer;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 48px;
    height: 48px;
    transform: translate(-50%, -50%);
  }
`;

const UnstyledEnergy = styled(Flex)<any>`
  ${height};
  ${border};
  ${borderRight};
  ${color};
  ${position};
  ${boxSizing};
  ${fontWeight};
  ${lineHeight};

  overflow: visible;

  &::after {
    content: ' ';
    position: absolute;
    left: 100%;
    height: 0;
    width: 0;
    border: solid transparent;
    border-left-color: ${props => props.bg};
    ${props => borderWidth({ ...props, borderWidth: props.arrowSize })};
  }

  &::before {
    content: ' ';
    position: absolute;
    left: 100%;
    height: 0;
    width: 0;
    top: -1px;
    border: solid transparent;
    border-left-color: black;
    ${props => borderWidth({ ...props, borderWidth: props.arrowBorderSize })};
  }
`;

const Energy = (props: ComponentProps<typeof UnstyledEnergy>) => (
  <UnstyledEnergy
    boxSizing="content-box"
    position="relative"
    textAlign="left"
    p="sp_0"
    fontSize="inherit"
    lineHeight="inherit"
    alignItems="stretch"
    fontWeight="bold"
    border="1px solid black"
    borderRight="none"
    {...props}
  />
);

const EnergyCode = styled.span<any>`
  ${space};
  ${color};
  ${fontSize};
  ${fontFamily};
  ${lineHeight};
  ${textShadow};
`;

const energyCodeStyle: ComponentProps<typeof EnergyCode> = {
  px: 1,
  color: 'sys.neutral.background.default',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  fontWeight: '700',
  textShadow: '-1px 0 black, 0 1px black, 1px 0 black, 0 -1px black',
};

const Superscript = styled.sup<
  MarginLeftProps & FontSizeProps & VerticalAlignProps & LineHeightProps
>`
  ${marginLeft};
  ${fontSize};
  ${verticalAlign};
  ${lineHeight};
`;

const superScriptStyle: ComponentProps<typeof Superscript> = {
  ml: '-1px',
  fontSize: '7px',
  verticalAlign: 'super',
  lineHeight: 0,
};

const formatEnergyClassCode = (code: string) => {
  const match = code.match(/^([A-G])(\+*)$/);
  if (!match || !match[2]) {
    return code;
  }

  return (
    <>
      {match[1]}
      <Superscript {...superScriptStyle}>{match[2]}</Superscript>
    </>
  );
};

const StyledImg = styled.img<HeightProps & FontSizeProps & BackgroundColorProps & WidthProps>`
  ${height};
  ${fontSize};
  ${backgroundColor};
  ${width};
`;

const imgStyles: ComponentProps<typeof StyledImg> = {
  fontSize: '50%',
  backgroundColor: 'sys.neutral.background.default',
  // deterministic width from figma, to avoid webkit overflow: visible issue
  width: '6px',
};

const EnergyTag = ({ energyClass, scaleType, href, onClick, size = 'small' }: EnergyTagProps) => {
  const { r, t } = useLocalization();
  const props: { as: ElementType; href?: string } = href
    ? { href, as: 'a' }
    : { as: onClick ? 'button' : 'span' };
  const isOldScale = scaleType === EnergyClassScaleType.Old;
  const [energyClassCode, energyClassColor] = isOldScale
    ? getOldEnergyClassMeta(energyClass)
    : getEnergyClassMeta(energyClass);
  const rangeSvg = isOldScale ? rangeOld : range;
  const rangeAlt = isOldScale ? 'D↑A+++' : 'G↑A';
  const ContainerComponent = onClick || href ? ContainerWithPointer : Container;
  const { labelHeight, arrowSize, arrowBorderSize } = SIZE_CONFIG[size];

  return (
    <object title={t('h24_energy_label')}>
      <ContainerComponent
        {...props}
        {...containerStyleProps}
        data-testid="article-energy"
        onClick={onClick}
        data-value={energyClassCode}
        aria-label={r.t('h24_accessibility_energy_class', {
          energyClass: energyClassCode,
        })}
      >
        <Energy
          height={labelHeight}
          lineHeight={labelHeight}
          mr={arrowBorderSize}
          bg={energyClassColor}
          arrowSize={arrowSize}
          arrowBorderSize={arrowBorderSize}
        >
          <StyledImg {...imgStyles} loading="lazy" src={rangeSvg} alt={rangeAlt} />

          <EnergyCode {...energyCodeStyle}>{formatEnergyClassCode(energyClassCode)}</EnergyCode>
        </Energy>
      </ContainerComponent>
    </object>
  );
};

export type { EnergyTagSize };
export default EnergyTag;
